import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FunnelProgress } from '@/components/health-check/FunnelChrome';
import { BhcLogo } from '@/components/health-check/BhcHeader';
import { loadIntake, saveIntake } from '@/lib/bhc';
import '@/styles/business-health-check.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/health-check`;
const MAX_ATTEMPTS = 8;

export default function ThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const intake = useMemo(() => loadIntake(), []);
  const isTrust = intake.variant === 'trust';
  const firstName = (intake.name || '').split(' ')[0];

  const [state, setState] = useState(sessionId ? 'checking' : 'paid'); // checking | paid | timeout | error
  const [receipt, setReceipt] = useState(intake.receipt || {});

  const poll = useCallback(async (attempt) => {
    if (attempt >= MAX_ATTEMPTS) { setState('timeout'); return; }
    try {
      const res = await fetch(`${API}/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        const r = { paidAt: new Date().toISOString(), amount: data.amount_display || '$4,995.00', sessionId, ref: data.engagement_ref || '' };
        setReceipt(r);
        saveIntake({ ...loadIntake(), receipt: r, status: 'paid' });
        setState('paid');
        return;
      }
      if (data.status === 'expired') { setState('error'); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { if (sessionId) poll(0); }, [sessionId, poll]);

  if (state === 'checking') {
    return (
      <div className="bhc-root">
        <Helmet><title>Confirming payment · KGOB</title>
        <meta name="description" content="Confirming your KGOB Health Check payment." /><meta name="robots" content="noindex" /></Helmet>
        <header className="fheader"><div className="wrap fheader__bar"><BhcLogo /></div></header>
        <main className="funnel"><div className="wrap funnel__grid funnel__grid--narrow">
          <div className="success" data-testid="thankyou-checking">
            <div className="spinner" style={{ margin: '0 auto 26px' }} />
            <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '32px', color: 'var(--navy-800)' }}>Confirming your payment…</h1>
            <p className="lede" style={{ marginTop: '14px' }}>One moment while we verify with Stripe. Please don't close this window.</p>
          </div>
        </div></main>
      </div>
    );
  }

  if (state === 'timeout' || state === 'error') {
    return (
      <div className="bhc-root">
        <Helmet><title>Payment status · KGOB</title><meta name="robots" content="noindex" /></Helmet>
        <header className="fheader"><div className="wrap fheader__bar"><BhcLogo /></div></header>
        <main className="funnel"><div className="wrap funnel__grid funnel__grid--narrow">
          <div className="success" data-testid="thankyou-pending">
            <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '32px', color: 'var(--navy-800)' }}>We're finalizing your payment.</h1>
            <p className="lede" style={{ marginTop: '14px', maxWidth: '54ch', marginInline: 'auto' }}>This can take a moment. A receipt and welcome email will arrive shortly. If you have any questions, call <a data-testid="thank-you-a-3" href="tel:17045993355" style={{ color: 'var(--navy-800)', borderBottom: '1px solid var(--gold-500)' }}>(704) 599-3355</a>.</p>
            <div className="actionbar" style={{ justifyContent: 'center', marginTop: '28px' }}><Link data-testid="thank-you-link-1" className="btn btn--ghost btn--lg" to="/business-health-check">Back to the Health Check</Link></div>
          </div>
        </div></main>
      </div>
    );
  }

  // paid
  return (
    <div className="bhc-root">
      <Helmet><title>You're a KGOB client · Health Check | KGOB</title><meta name="robots" content="noindex" /></Helmet>
      <header className="fheader"><div className="wrap fheader__bar">
        <BhcLogo />
        <span className="fheader__secure" style={{ marginLeft: 'auto' }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg> Payment confirmed</span>
      </div></header>
      <FunnelProgress step={4} />

      <main className="funnel">
        <div className="wrap">
          <div className="success" data-testid="thankyou-paid">
            <div className="success__badge"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg></div>
            <span className="eyebrow eyebrow--center">Payment confirmed</span>
            <h1 style={{ marginTop: '14px' }}>{firstName ? `You're a KGOB client, ${firstName}.` : "You're a KGOB client."} Here's what's next.</h1>
            <p className="lede" style={{ marginTop: '20px', maxWidth: '60ch', marginInline: 'auto' }}>Your {isTrust ? 'Trust & Estate' : 'Mid-Year Business'} Health Check is confirmed. A member of our CPA team has been assigned to your engagement and will reach out within <strong>one business day</strong>.</p>
            <div className="receiptbar">
              <span>Receipt: <b>{intake.email || 'your inbox'}</b></span>
              <span>Amount: <b>{receipt.amount || '$4,995.00'}</b></span>
              {(receipt.ref || receipt.sessionId) && <span>Ref: <b data-testid="thankyou-engagement-ref">{receipt.ref || receipt.sessionId.slice(0, 18)}</b></span>}
            </div>
          </div>

          <div style={{ maxWidth: '900px', margin: '48px auto 0' }}>
            <div className="steps-next">
              <div className="panel"><div className="n">1</div><h4>Schedule your intake call</h4><p>Book a short kickoff call so we can confirm the records we need and get started.</p></div>
              <div className="panel"><div className="n">2</div><h4>Upload your records</h4><p>Check your inbox for a secure link and a simple checklist. The faster we receive everything, the faster you get your report.</p></div>
              <div className="panel"><div className="n">3</div><h4>We get to work</h4><p>We deliver your written findings within <strong>15 business days of receiving your complete records</strong> — then we'll schedule your 60-minute strategy session.</p></div>
            </div>
            <div className="actionbar" style={{ justifyContent: 'center', marginTop: '40px' }}>
              <Link className="btn btn--primary btn--lg" to="/business-health-check/schedule" data-testid="schedule-cta">Schedule your intake call <span className="arrow">→</span></Link>
              <a data-testid="thank-you-a-2" className="btn btn--ghost btn--lg" href="#checklist">Go to document checklist</a>
            </div>
            <p className="center muted" style={{ marginTop: '18px', fontSize: '13.5px' }}>A receipt and welcome email are on their way. Need help now? Call <a data-testid="thank-you-a-1" href="tel:17045993355" style={{ color: 'var(--navy-800)', borderBottom: '1px solid var(--gold-500)' }}>(704) 599-3355</a>.</p>

            <div className="panel" id="checklist" style={{ marginTop: '44px' }}>
              <p className="panel__title">Your secure document checklist</p>
              <p className="panel__hint">Upload through the secure portal link in your email — never by email attachment. The 15-day clock starts when this is complete.</p>
              <ul className="summary__incl" style={{ marginTop: '18px' }}>
                <li style={{ color: 'var(--ink)' }}><span className="tick" style={{ color: 'var(--muted)' }}>○</span> Bank &amp; credit-card statements, Jan 1 – Jun 30</li>
                <li style={{ color: 'var(--ink)' }}><span className="tick" style={{ color: 'var(--muted)' }}>○</span> Access to your accounting file (QuickBooks/Xero) or exports</li>
                <li style={{ color: 'var(--ink)' }}><span className="tick" style={{ color: 'var(--muted)' }}>○</span> Most recent filed business tax return</li>
                <li style={{ color: 'var(--ink)' }}><span className="tick" style={{ color: 'var(--muted)' }}>○</span> Payroll reports (if you run payroll)</li>
                <li style={{ color: 'var(--ink)' }}><span className="tick" style={{ color: 'var(--muted)' }}>○</span> Signed Form 2848 (we'll send it for e-signature)</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
